import { PasswordInput } from '@/components/blocks/PasswordInput';
import { ChangePasswordValues } from '@/data/Content/PersonInfo';
import { useForm } from '@/utils/useForm';
import { Stack, Button, Typography, Grid } from '@mui/material';
import { FC, useMemo } from 'react';
import { escapeRegExp } from 'lodash';
import { AccountPersonalButtonSX } from '@/components/content/Account/styles/accountPersonal';
import {
	AccountChangePasswordFormDescriptionSX,
	AccountChangePasswordFormTitleSX,
} from '@/components/content/Account/styles/accountChangePasswordForm';
import { EMPTY_STRING } from '@/data/constants/marketing';
import { addressFormFieldsSX } from '@/components/blocks/AddressForm/styles/fields';
import { PasswordValidations } from '@/components/content/Register/parts/PasswordValidations';
import { DpButton } from '@/components/blocks/DpLibrary/DpButton';

const initialPasswordValue: ChangePasswordValues = {
	xcred_logonPasswordOld: '',
	logonPassword: '',
	xcred_logonPasswordVerify: '',
};

type Prop = {
	onSubmit: (value: ChangePasswordValues) => void;
	isLoading?: boolean;
};

const PASSWORD_MIN_LENGTH = 8;
const PASSWORD_REGEX = /^(?=.*\d).{8,}$/;

export const AccountChangePasswordForm: FC<Prop> = ({ onSubmit, isLoading }) => {
	const { handleSubmit, values, handleInputChange, formRef, error, resetForm } =
		useForm<ChangePasswordValues>(initialPasswordValue);

	const areAllValuesEmpty = () =>
		Object.values(values).every(
			(value) =>
				value === null ||
				value === undefined ||
				(typeof value === 'string' && value.trim() === '') ||
				(Array.isArray(value) && value.length === 0) ||
				(typeof value === 'object' && !Array.isArray(value) && Object.keys(value).length === 0)
		);

	const detailedErrors = useMemo(
		() => ({
			logonPassword: !values.logonPassword
				? areAllValuesEmpty()
					? ''
					: 'Captura una contraseña nueva'
				: values.logonPassword.length < PASSWORD_MIN_LENGTH
				? `La contraseña debe tener al menos ${PASSWORD_MIN_LENGTH} caracteres`
				: !PASSWORD_REGEX.test(values.logonPassword)
				? 'La contraseña debe incluir al menos un número'
				: EMPTY_STRING,

			xcred_logonPasswordVerify:
				values.logonPassword &&
				values.xcred_logonPasswordVerify &&
				values.logonPassword !== values.xcred_logonPasswordVerify
					? 'Las contraseñas no coinciden'
					: EMPTY_STRING,
		}),
		// eslint-disable-next-line react-hooks/exhaustive-deps
		[values.logonPassword, values.xcred_logonPasswordVerify]
	);

	const helperText = useMemo(
		() => ({
			xcred_logonPasswordOld: error.xcred_logonPasswordOld
				? 'Captura tu contraseña actual'
				: EMPTY_STRING,
			logonPassword: detailedErrors.logonPassword,
			xcred_logonPasswordVerify: detailedErrors.xcred_logonPasswordVerify,
		}),
		[error, detailedErrors]
	);

	const handleFormSubmit = (_values: ChangePasswordValues) => {
		if (detailedErrors.logonPassword || detailedErrors.xcred_logonPasswordVerify) {
			return;
		}
		onSubmit(_values);
		resetForm();
	};

	const passwordValidation = (password: any) => ({
		Length: password.length >= 8,
		Number: /\d{1,}/.test(password),
		Mayus: /[A-Z]{1,}/.test(password),
	});

	return (
		<Stack
			component="form"
			noValidate
			ref={formRef}
			display="block"
			spacing={2}
			onSubmit={handleSubmit(handleFormSubmit)}
		>
			<Stack>
				<Typography component={'h2'} sx={AccountChangePasswordFormTitleSX}>
					{'CAMBIAR CONTRASEÑA'}
				</Typography>
				<Typography sx={AccountChangePasswordFormDescriptionSX}>
					{'Para cambiar la contraseña necesitas ingresar tu contraseña actual.'}
				</Typography>
			</Stack>
			<PasswordInput
				value={values.xcred_logonPasswordOld}
				required
				label="Contraseña actual"
				error={!!error.xcred_logonPasswordOld}
				helperText={helperText.xcred_logonPasswordOld}
				name="xcred_logonPasswordOld"
				onChange={handleInputChange}
				sx={addressFormFieldsSX}
			/>
			<Grid>
				<PasswordInput
					value={values.logonPassword}
					required
					label="Contraseña nueva"
					name="logonPassword"
					onChange={handleInputChange}
					inputProps={{
						maxLength: 100,
						pattern: escapeRegExp(values.xcred_logonPasswordVerify),
					}}
					sx={addressFormFieldsSX}
				/>
				{values.logonPassword && (
					<PasswordValidations
						validations={{
							...passwordValidation(values.logonPassword),
						}}
					/>
				)}
			</Grid>
			<PasswordInput
				value={values.xcred_logonPasswordVerify}
				required
				error={!!helperText.xcred_logonPasswordVerify}
				label="Confirmar contraseña nueva"
				name="xcred_logonPasswordVerify"
				helperText={helperText.xcred_logonPasswordVerify}
				onChange={handleInputChange}
				inputProps={{
					maxLength: 100,
					pattern: escapeRegExp(values.logonPassword),
				}}
				sx={addressFormFieldsSX}
			/>
			<DpButton
				id="change-password-dialog-submit"
				data-testid="change-password-dialog-submit"
				type="submit"
				variant="primary"
				fullWidth
				sx={{ marginTop: 2 }}
				disabled={
					isLoading ||
					!!helperText.logonPassword ||
					!!helperText.xcred_logonPasswordOld ||
					!!helperText.xcred_logonPasswordVerify
				}
			>
				{'Cambiar contraseña'}
			</DpButton>
		</Stack>
	);
};
